import type { RecordingDB } from './recording-db';
import { uploadChunkToS3 } from './s3-upload';
import type { S3PartRecord, PresignedUrlItem } from '@clarix/shared-types';
import { UPLOAD_MAX_RETRIES, UPLOAD_RETRY_DELAYS_MS } from './constants';
import { logger } from '@clarix/utils';

/** S3 multipart minimum part size: 5 MB (except the final part) */
const S3_MIN_PART_BYTES = 5 * 1024 * 1024;

type MoreUrlsFn = (payload: {
  upload_id: string;
  next_part_number: number;
}) => Promise<{ presigned_urls: PresignedUrlItem[] }>;

/**
 * Manages the S3 multipart upload queue for recording.
 *
 * Design decisions:
 *   - Reads S3 parts from IndexedDB (queued by useRecordingControls as audio blobs
 *     accumulated to the minimum part size).
 *   - Fetches a FRESH presigned URL just-in-time before each upload — no pre-cached
 *     URLs that could expire while the tab is inactive.
 *   - Processes one part at a time (sequential) to guarantee S3 part-number ordering.
 *   - Retries failed uploads with exponential backoff before giving up.
 *   - drain() uploads all remaining queued parts regardless of size — call this on stop().
 */
export class ChunkUploadQueue {
  private readonly db: RecordingDB;
  private readonly recordingId: string;
  private readonly uploadId: string;
  private readonly moreUrlsFn: MoreUrlsFn;
  private readonly onError?: (error: unknown) => void;

  private isProcessing = false;
  private isDraining = false;
  private isCancelled = false;

  constructor(
    db: RecordingDB,
    recordingId: string,
    uploadId: string,
    moreUrlsFn: MoreUrlsFn,
    /** A background pass failed; drain() still throws its own failure to the caller. */
    onError?: (error: unknown) => void,
  ) {
    this.db = db;
    this.recordingId = recordingId;
    this.uploadId = uploadId;
    this.moreUrlsFn = moreUrlsFn;
    this.onError = onError;
  }

  /**
   * Notify the queue that new parts may be available in IndexedDB.
   * Triggers a processing pass if one is not already running.
   * Call this whenever a new part is saved to the DB.
   */
  notify(): void {
    if (!this.isProcessing && !this.isCancelled) {
      this.processQueue().catch((err: unknown) => this.onError?.(err));
    }
  }

  /**
   * Wait for all currently-queued parts (including the final partial part) to be
   * uploaded to S3.  Call this before adapter.complete().
   *
   * Sets isDraining = true so processQueue() uploads even sub-minimum-size parts.
   */
  async drain(): Promise<void> {
    logger.log('[Recording/Queue] drain() called — uploading all remaining parts');
    this.isDraining = true;
    // Trigger one final processing pass
    if (!this.isProcessing) {
      await this.processQueue();
    } else {
      // Wait for the in-flight pass to finish, then do one more
      await this.waitForIdle();
      await this.processQueue();
    }
    logger.log('[Recording/Queue] drain() complete');
  }

  /** Cancel all future processing (used on abort). */
  cancel(): void {
    this.isCancelled = true;
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.isCancelled) return;
    this.isProcessing = true;

    try {
      while (!this.isCancelled) {
        const pending = await this.db.getPendingParts(this.recordingId);
        if (pending.length === 0) break;

        // Merge consecutive IDB records into a single blob that meets the S3
        // 5 MB minimum. In draining mode we upload whatever we have (final part
        // is allowed to be smaller per S3 multipart rules).
        const mergedSize = pending.reduce((sum, r) => sum + r.blob.size, 0);

        if (!this.isDraining && mergedSize < S3_MIN_PART_BYTES) {
          // Not enough data yet and more is expected — wait
          break;
        }

        // Collect records to merge into one S3 part
        const toMerge: S3PartRecord[] = [];
        let accumulated = 0;
        for (const record of pending) {
          toMerge.push(record);
          accumulated += record.blob.size;
          if (accumulated >= S3_MIN_PART_BYTES) break; // have enough for one S3 part
        }

        logger.log(
          `[Recording/Queue] merging ${toMerge.length} IDB record(s) into 1 S3 part` +
            ` — total: ${(accumulated / 1024).toFixed(1)} KB, draining: ${this.isDraining}`,
        );

        await this.uploadMergedPart(toMerge);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Merge one or more IDB records into a single Blob and upload it as one S3 part.
   * Uses the s3_part_number of the FIRST record as the canonical S3 part number
   * and marks all merged records as done with the same etag.
   */
  private async uploadMergedPart(records: S3PartRecord[]): Promise<void> {
    if (records.length === 0) return;

    // noUncheckedIndexedAccess: guard even though the length check above makes this safe
    const firstRecord = records[0];
    if (!firstRecord || firstRecord.id === undefined) return;

    const mimeType = firstRecord.blob.type || 'audio/mp4';
    const merged = new Blob(
      records.map((r) => r.blob),
      { type: mimeType },
    );
    const s3PartNumber = firstRecord.s3_part_number;

    logger.log(
      `[Recording/Queue] uploading S3 part ${s3PartNumber} — merged size: ${(merged.size / 1024).toFixed(1)} KB from ${records.length} IDB record(s)`,
    );

    // Mark all as uploading
    for (const r of records) {
      if (r.id !== undefined) await this.db.markUploading(r.id);
    }

    try {
      logger.log(`[Recording/Queue] fetching fresh presigned URL for S3 part ${s3PartNumber}...`);
      const urlResponse = await this.moreUrlsFn({
        upload_id: this.uploadId,
        next_part_number: s3PartNumber,
      });

      const urlItem = urlResponse.presigned_urls[0];
      if (!urlItem) throw new Error(`No presigned URL returned for part ${s3PartNumber}`);

      const etag = await this.uploadWithRetry(urlItem.url, merged, s3PartNumber);
      logger.log(`[Recording/Queue] ✓ S3 part ${s3PartNumber} uploaded — etag: ${etag}`);

      // Mark all merged IDB records as done with the same etag
      for (const r of records) {
        if (r.id !== undefined) await this.db.markDone(r.id, etag);
      }
    } catch (err) {
      logger.error(`[Recording/Queue] ✗ S3 part ${s3PartNumber} failed:`, err);
      for (const r of records) {
        if (r.id !== undefined) await this.db.markFailed(r.id);
      }
      throw err;
    }
  }

  private async uploadWithRetry(url: string, blob: Blob, partNumber: number): Promise<string> {
    let lastError: unknown;

    for (let attempt = 1; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
      try {
        logger.log(
          `[Recording/Queue] S3 PUT part ${partNumber}, attempt ${attempt}/${UPLOAD_MAX_RETRIES}`,
        );
        const etag = await uploadChunkToS3(url, blob);
        return etag;
      } catch (err) {
        lastError = err;
        logger.error(`[Recording/Queue] S3 PUT part ${partNumber} attempt ${attempt} failed:`, err);
        if (attempt < UPLOAD_MAX_RETRIES) {
          const delay = UPLOAD_RETRY_DELAYS_MS[attempt - 1] ?? 4000;
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    }

    // The original error, so callers can tell a network wait from a real failure.
    logger.error(`[Recording/Queue] S3 part ${partNumber} failed after ${UPLOAD_MAX_RETRIES} attempts`);
    throw lastError;
  }

  private async waitForIdle(): Promise<void> {
    while (this.isProcessing) {
      await new Promise((r) => setTimeout(r, 100));
    }
  }
}
